/** TanStack Query hooks for admin subscription management. */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { api } from '@/shared/api/client';
import type {
  GrantSubscriptionInput,
  ChangeTierInput,
  CancelSubscriptionInput,
  ReactivateSubscriptionInput,
  SubscriptionAuditLogEntry,
} from '@formency/shared';
import { extractErrorMessage } from '@/shared/utils/extractErrorMessage';
import { subscriptionKeys } from './useSubscription';
import { adminUserKeys } from './useAdminUsers';

// ─── Types ──────────────────────────────────────────────────────────────────

/**
 * Active subscription row as returned by the admin endpoint. Mirrors the
 * Prisma model with snake_case field names — kept loose since this is purely
 * an admin view and the schema may evolve.
 */
export interface AdminSubscriptionRow {
  id: string;
  user_id: string;
  tier: 'plus' | 'pro' | 'max';
  status: 'active' | 'trialing' | 'past_due' | 'canceled' | 'incomplete' | 'unpaid';
  source: 'stripe' | 'admin' | 'trial';
  current_period_start: string | null;
  current_period_end: string | null;
  cancel_at_period_end: boolean;
  external_customer_id: string | null;
  external_subscription_id: string | null;
  price_id: string | null;
  granted_by: string | null;
  note: string | null;
  created_at: string;
  updated_at: string;
}

// ─── Query keys ─────────────────────────────────────────────────────────────

export const adminSubscriptionKeys = {
  all: ['f', 'admin-subscriptions'] as const,
  forUser: (userId: string) => [...adminSubscriptionKeys.all, 'for-user', userId] as const,
  audit: (subscriptionId: string) =>
    [...adminSubscriptionKeys.all, 'audit', subscriptionId] as const,
};

// ─── Queries ────────────────────────────────────────────────────────────────

/** Fetch the user's active subscription row (or null). */
export function useAdminUserSubscription(userId: string | undefined) {
  return useQuery<AdminSubscriptionRow | null>({
    queryKey: adminSubscriptionKeys.forUser(userId!),
    queryFn: () =>
      api.get<AdminSubscriptionRow | null>(`/admin/subscriptions/users/${userId}/active`),
    enabled: !!userId,
    staleTime: 30_000,
  });
}

/** Fetch the audit log for a subscription. */
export function useAdminSubscriptionAuditLog(subscriptionId: string | undefined) {
  return useQuery<SubscriptionAuditLogEntry[]>({
    queryKey: adminSubscriptionKeys.audit(subscriptionId!),
    queryFn: () =>
      api.get<SubscriptionAuditLogEntry[]>(`/admin/subscriptions/${subscriptionId}/audit-log`),
    enabled: !!subscriptionId,
    staleTime: 30_000,
  });
}

// ─── Mutations ──────────────────────────────────────────────────────────────

function invalidateForUser(qc: ReturnType<typeof useQueryClient>, userId: string) {
  qc.invalidateQueries({ queryKey: adminSubscriptionKeys.forUser(userId) });
  qc.invalidateQueries({ queryKey: adminSubscriptionKeys.all });
  qc.invalidateQueries({ queryKey: adminUserKeys.detail(userId) });
  // Also invalidate the learner's own /me/subscription cache in case the admin
  // is operating on themselves or shares the cache namespace.
  qc.invalidateQueries({ queryKey: subscriptionKeys.all });
}

export function useGrantSubscription() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: GrantSubscriptionInput) =>
      api.post<AdminSubscriptionRow>('/admin/subscriptions', input),
    onSuccess: (_, input) => {
      invalidateForUser(qc, input.userId);
      toast.success('Subscription granted');
    },
    onError: (error: unknown) => {
      toast.error(extractErrorMessage(error, 'Failed to grant subscription'));
    },
  });
}

export function useChangeSubscriptionTier() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      subscriptionId,
      input,
    }: {
      subscriptionId: string;
      userId: string;
      input: ChangeTierInput;
    }) => api.patch<AdminSubscriptionRow>(`/admin/subscriptions/${subscriptionId}/tier`, input),
    onSuccess: (_, { userId, subscriptionId }) => {
      invalidateForUser(qc, userId);
      qc.invalidateQueries({ queryKey: adminSubscriptionKeys.audit(subscriptionId) });
      toast.success('Tier updated');
    },
    onError: (error: unknown) => {
      toast.error(extractErrorMessage(error, 'Failed to change tier'));
    },
  });
}

export function useCancelSubscription() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      subscriptionId,
      input,
    }: {
      subscriptionId: string;
      userId: string;
      input: CancelSubscriptionInput;
    }) => api.post<AdminSubscriptionRow>(`/admin/subscriptions/${subscriptionId}/cancel`, input),
    onSuccess: (_, { userId, subscriptionId }) => {
      invalidateForUser(qc, userId);
      qc.invalidateQueries({ queryKey: adminSubscriptionKeys.audit(subscriptionId) });
      toast.success('Subscription cancelled');
    },
    onError: (error: unknown) => {
      toast.error(extractErrorMessage(error, 'Failed to cancel subscription'));
    },
  });
}

export function useReactivateSubscription() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      subscriptionId,
      input,
    }: {
      subscriptionId: string;
      userId: string;
      input: ReactivateSubscriptionInput;
    }) =>
      api.post<AdminSubscriptionRow>(`/admin/subscriptions/${subscriptionId}/reactivate`, input),
    onSuccess: (_, { userId, subscriptionId }) => {
      invalidateForUser(qc, userId);
      qc.invalidateQueries({ queryKey: adminSubscriptionKeys.audit(subscriptionId) });
      toast.success('Subscription reactivated');
    },
    onError: (error: unknown) => {
      toast.error(extractErrorMessage(error, 'Failed to reactivate subscription'));
    },
  });
}
